/**
 * App entrypoint.
 *
 * Boot order (Phase 7+):
 *   1. `await fetchSession()` — the officer session is resolved
 *      pre-mount so the first paint is the authenticated state and the
 *      `AppHeader` / `GuildOnboardingPanel` render correctly from the
 *      very first frame.
 *   2. Create the app + Pinia + the host router.
 *   3. `await installApp(app)` — provides Apollo, the CONFIRM port,
 *      the session port, and the guild port.
 *   4. `installGvgPlanner(app, router, ...)` — adds the planner's 6
 *      routes, wires the active-guild cascade, contributes the planner's
 *      nav descriptors, and bootstraps `loadAvailableGuilds()`.
 *   5. `app.use(router)` — runs Vue Router's `install(app)` AFTER every
 *      feature route is in the matcher. The `install()` hook fires the
 *      initial navigation synchronously via
 *      `push(routerHistory.location)`; if it ran before the feature
 *      routes were added, the initial `'/'` would resolve to the host
 *      redirect `{ name: 'calendar' }` and `MATCHER_NOT_FOUND` would
 *      surface through Vue Router's devtools overlay as
 *      `Uncaught Error: No match for {"name":"calendar",…}`.
 *   6. Mount.
 */
import { createApp, h, markRaw } from 'vue'
import { createPinia } from 'pinia'
import { createVuestic, useColors } from 'vuestic-ui'

import App from '@/App.vue'
import { fetchSession } from '@/app/session'
import { installApp } from '@/app/index'
import { createHostRouter } from '@/app/router'
import { addNavLinks } from '@/app/nav'
import { installGvgPlanner } from '@/features/gvg-planner'
import VuesticCheckboxCheckIcon from '@/shared/ui/VuesticCheckboxCheckIcon.vue'
import VuesticCheckboxIndeterminateIcon from '@/shared/ui/VuesticCheckboxIndeterminateIcon.vue'

/**
 * Style layer order matters:
 *   1. Tailwind v4 base + tokens (CSS custom properties the bridge below
 *      forwards into Vuestic).
 *   2. Vuestic's `essential.css` — skips the box-sizing reset that the
 *      full bundle ships, which would fight Tailwind v4's own reset. The
 *      `essential.css` sheet is also the only one we need for the
 *      components the `App*` wrappers consume (button / modal / select /
 *      input / checkbox / dropdown / chip). See `apps/frontend/AGENTS.md`
 *      for the rationale.
 *   3. App-local SCSS (the `#app` shell + remaining SCSS variables).
 */
import '@/shared/design/tailwind.css'
import 'vuestic-ui/styles/essential.css'
import '@/shared/design/global.scss'

await fetchSession()

const app = createApp(App)
app.use(createPinia())

/**
 * Register Vuestic with a hex-value token bridge — every Vuestic semantic
 * colour (`--va-primary`, `--va-background-primary`, …) is set to the
 * same hex value as our design tokens in `src/shared/design/tailwind.css`
 * (`@theme { ... }`).
 *
 * Note: Vuestic validates each colour string at boot via its own colour
 * parser, which does NOT resolve `var(--…)` references. The bridge
 * therefore ships literal hexes here, mirroring the token values from
 * `tailwind.css`. If you change a token in `tailwind.css`, mirror the
 * change here so the two stay in sync — both files are the source of
 * truth for their respective layers (Tailwind utilities + Vuestic
 * components). The hex literals below must match `tailwind.css` exactly.
 *
 * `useColors().applyPreset('dark')` is invoked once at boot to pin the
 * theme to dark — Vuestic ships a light preset by default, and the wuxia
 * palette in `tailwind.css` is the dark variant.
 */
app.use(
  createVuestic({
    config: {
      colors: {
        variables: {
          default: '#c9a24a',
          primary: '#c9a24a',
          secondary: '#d4b15c',
          success: '#6cb887',
          warning: '#d4a35a',
          danger: '#d96a5e',
          info: '#6a9bd1',
          backgroundPrimary: '#0a0c12',
          backgroundSecondary: '#141821',
          backgroundElement: '#1c212c',
          backgroundBorder: '#2a2f3a',
          textPrimary: '#ede4d0',
          textInverted: '#0a0c12',
        },
      },
      /**
       * Replace the Material-Icons font glyphs `VaCheckbox` consumes
       * (`va-check` for the checked state, `remove` for indeterminate) with
       * an inline-SVG component. The Material Icons font is intentionally
       * NOT loaded (≈300 KB), so the default fallback renders the literal
       * icon name as text inside the checkbox square — the bug visible on
       * `/settings`. Registering a `component` here points Vuestic at the
       * SVG instead of the font, matching the zero-font contract used by
       * `AppIcon`. See `shared/ui/VuesticCheckboxCheckIcon.vue` and
       * `VuesticCheckboxIndeterminateIcon.vue` for the icon paths and
       * rationale.
       *
       * `VaInput` validation also requests `va-warning`; include its SVG
       * here because this array replaces Vuestic's default icon registry.
       *
       * The entries go into the flat `icons` array (not the
       * `{ aliases, fonts }` shape used by `createIconsConfig`) — that
       * helper expands to a flat array internally, and `GlobalConfig.icons`
       * is typed as the flat shape, so the partial config inherits it.
       */
      icons: [
        { name: 'va-check', component: markRaw(VuesticCheckboxCheckIcon) },
        { name: 'remove', component: markRaw(VuesticCheckboxIndeterminateIcon) },
        {
          name: 'va-warning',
          component: markRaw({
            render: () => h('svg', {
              class: 'va-icon-svg',
              width: '1em',
              height: '1em',
              viewBox: '0 0 24 24',
              fill: 'none',
              stroke: 'currentColor',
              'stroke-width': 2,
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round',
              'aria-hidden': 'true',
              focusable: 'false',
            }, h('path', { d: 'M12 3 2 21h20L12 3ZM12 9v5M12 17v.01' })),
          }),
        },
      ],
    },
  }),
)
useColors().applyPreset('dark')

const router = createHostRouter()
await installApp(app)

installGvgPlanner(app, router, { contributeNav: addNavLinks })

app.use(router)

app.mount('#app')
